/**
 * Preset profile avatars.
 *
 * A closed set of 32 house-style clay portraits plus a gender-neutral default,
 * all rehosted on the MaJourneys Cloudflare Images account. Every one was
 * generated image-to-image against avatar #10 so the crop, pose and lighting
 * are identical across the set — that uniformity is the whole point, so new
 * entries must be produced the same way rather than from a fresh text prompt.
 *
 * Shared rather than duplicated per app because the API validates against this
 * list: `users.image` is written straight from a client request, so without a
 * closed set a caller could point their avatar at any URL on the internet.
 */

export const AVATAR_CDN = "https://imagedelivery.net/9h9OrUsEhPYeYHCr6DaDTQ";

export type AvatarGender = "m" | "f";

export interface PresetAvatar {
  /** Stable key stored nowhere — the URL is what persists. Useful for tests and keys. */
  readonly id: string;
  /** Cloudflare Images id. */
  readonly file: string;
  readonly label: string;
  /**
   * How the portrait reads, for the picker's filter only — it says nothing
   * about the person choosing it, and is never sent to the server. Assigned by
   * looking at each render, not inferred from the label.
   */
  readonly gender?: AvatarGender;
}

/** Shown when a user has not chosen one: a faceless clay bust, deliberately not male or female. */
export const NEUTRAL_AVATAR: PresetAvatar = {
  id: "neutral",
  file: "8ba281a5-1e00-4c2e-1e33-24395f4a4f00",
  label: "No avatar",
};

export const PRESET_AVATARS: readonly PresetAvatar[] = [
  { id: "a01", file: "fbc1e35b-c3da-4bfb-756f-db44eb6efd00", label: "Curly hair · hoodie" , gender: "m" },
  { id: "a02", file: "420cb6b5-c0ae-41ef-8f5f-3fa26c265800", label: "Salt-and-pepper beard" , gender: "m" },
  { id: "a03", file: "eff27024-517e-41f0-6be6-228cc6d54700", label: "Bald · white beard" , gender: "m" },
  { id: "a04", file: "d25ee7dd-6432-4c45-1542-cad59d2e0f00", label: "Blonde · gold beanie" , gender: "m" },
  { id: "a05", file: "eafea309-81fd-4f49-170d-7e33fc0c6300", label: "Full beard · navy shirt" , gender: "m" },
  { id: "a06", file: "df1ed1d6-8c26-4e53-6d6d-44f3c6270900", label: "Gold glasses · turtleneck" , gender: "m" },
  { id: "a07", file: "77ab567f-8ca7-4f3b-c4e6-0fc1bea37400", label: "Mustache · gold buttons" , gender: "m" },
  { id: "a08", file: "6b2f8385-014b-43a3-b363-4314dea6fd00", label: "Red hair · denim" , gender: "m" },
  { id: "a09", file: "192e84b4-166e-452e-59f1-15a9fd0a9200", label: "Gray hair · cardigan" , gender: "m" },
  { id: "a10", file: "024e9dd7-3054-4d80-067b-58fb6d462800", label: "Afro · gold headphones" , gender: "m" },
  { id: "a11", file: "c5ee6e53-d85a-4b4f-1abc-c1bb161b4f00", label: "Curly hair · gold hoops" , gender: "f" },
  { id: "a12", file: "c54b8bb4-f4ce-4bc3-ab80-463afedd7500", label: "Navy hijab · gold trim" , gender: "f" },
  { id: "a13", file: "c02398de-5dbb-4d7c-2b77-6dcfa03f9400", label: "Blonde bob · blazer" , gender: "f" },
  { id: "a14", file: "cbd27715-4038-42e1-4232-e67198650800", label: "Bangs · gold clip" , gender: "f" },
  { id: "a15", file: "b14c8340-edf2-4aef-ec0e-9d3df13c8800", label: "Silver bun · gold glasses" , gender: "f" },
  { id: "a16", file: "8f3c6e30-9e7c-4d08-d363-a89091507e00", label: "Box braids · gold hoops" , gender: "f" },
  { id: "a17", file: "d8c511d8-00bb-41e7-ea90-ab38789d1e00", label: "Long hair · gold detail" , gender: "f" },
  { id: "a18", file: "69d204cc-e289-4469-5280-2c4389af3d00", label: "Red wavy hair" , gender: "f" },
  { id: "a19", file: "26d8e4a2-03e1-4f00-c843-7752b052c300", label: "Pixie cut · turtleneck" , gender: "m" },
  { id: "a20", file: "2ae52672-0e8b-43f5-41bc-09cae2200000", label: "Styled hair · gold chain" , gender: "m" },
  { id: "a21", file: "71088646-ec25-4267-5f64-02897555e700", label: "Short silver hair" , gender: "f" },
  { id: "a22", file: "86819428-b6f0-476e-c084-74bb2f76c900", label: "Cropped hair · polo" , gender: "m" },
  { id: "a23", file: "9489fdb4-df9d-4838-c95c-f7e01f264e00", label: "Stubble · navy henley" , gender: "m" },
  { id: "a24", file: "44f1cb77-b388-405c-7ef3-677fb6638c00", label: "Twists · gold stud" , gender: "m" },
  { id: "a25", file: "8ceecdb6-dc94-4591-6eac-6da4b322dc00", label: "Blonde buzz · polo" , gender: "m" },
  { id: "a26", file: "d3e829b0-6432-4bea-55bc-4580906f7800", label: "Glasses · sweater vest" , gender: "m" },
  { id: "a27", file: "03ccc9a4-4eba-4deb-a422-ebfe0d912100", label: "Undercut · bomber" , gender: "m" },
  { id: "a28", file: "9af242a4-136e-46b9-32a1-636d7d982500", label: "Wavy hair · gold hoops" , gender: "f" },
  { id: "a29", file: "9dbfd90a-10b5-486c-79c3-4451b9bd5200", label: "Afro · gold earrings" , gender: "f" },
  { id: "a30", file: "a49bbd41-9572-4a86-fed5-affaaf246c00", label: "Long hair · gold necklace" , gender: "f" },
  { id: "a31", file: "93519b07-bb27-43eb-cb56-4cfe627c5900", label: "Blonde ponytail" , gender: "f" },
  { id: "a32", file: "78ca0db3-3482-4259-0532-39dff0b45e00", label: "Short gray · gold glasses" , gender: "f" },];

/** Full-square variant URL for an avatar. */
export function avatarUrl(a: PresetAvatar): string {
  return `${AVATAR_CDN}/${a.file}/public`;
}

/** Every URL the API will accept, including the neutral default. */
export const ALLOWED_AVATAR_URLS: readonly string[] = [
  ...PRESET_AVATARS.map(avatarUrl),
  avatarUrl(NEUTRAL_AVATAR),
];

/**
 * Guard for the API. `null` is valid — it clears the avatar back to the
 * neutral default, which is rendered client-side rather than stored.
 */
export function isAllowedAvatar(value: string | null | undefined): boolean {
  if (value == null) return true;
  return ALLOWED_AVATAR_URLS.includes(value);
}
